import type { RestClient } from "./restClient";

/** Fired after an avatar upload/removal so the persistent sidebar re-fetches
 *  the account photo without a full reload. */
export const AVATAR_CHANGED_EVENT = "egpt:avatar-changed";

export interface AppearancePref {
  /** Optional/legacy: color mode is a per-device client preference kept in
   *  localStorage and no longer persisted server-side. Older rows may still
   *  carry it; nothing reads it. */
  mode?: string;
  /** Legacy single accent (pre per-mode). Older rows carry it; read only for
   *  migration in AppearanceSync, never written anymore. */
  accent?: string;
  /** Per-mode accents (current shape) — light and dark are independent. */
  accentLight?: string;
  accentDark?: string;
}

export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  preferred_name: string;
  has_avatar: boolean;
  appearance: AppearancePref | null;
  password_change_url: string;
  name_changed?: boolean;
}

export interface ProfilePatch {
  first_name?: string;
  last_name?: string;
  preferred_name?: string;
  appearance?: AppearancePref;
}

export class ProfileApi {
  constructor(
    private client: RestClient,
    private apiUrl: string = import.meta.env.VITE_API_URL ?? "",
  ) {}

  get(): Promise<UserProfile> {
    return this.client.get("/v1/profile");
  }

  update(patch: ProfilePatch): Promise<UserProfile> {
    return this.client.patch("/v1/profile", patch);
  }

  async uploadAvatar(blob: Blob): Promise<{ has_avatar: boolean }> {
    const form = new FormData();
    form.append("file", blob, "avatar.webp");
    // RestClient.raw sets credentials; do NOT set Content-Type — the browser
    // adds the multipart boundary.
    const r = await this.client.raw("/v1/profile/avatar", { method: "PUT", body: form });
    return (await r.json()) as { has_avatar: boolean };
  }

  deleteAvatar(): Promise<{ has_avatar: boolean }> {
    return this.client.delete("/v1/profile/avatar");
  }

  /** Same-origin URL for an <img src>. Pass a version to cache-bust after upload. */
  avatarHref(version?: number): string {
    const base = `${this.apiUrl.replace(/\/+$/, "")}/v1/profile/avatar`;
    return version === undefined ? base : `${base}?v=${version}`;
  }
}
